import { useReducer, useRef, useEffect } from 'react';

/**
 * useAnimatedPresence - Manages mount/unmount lifecycle for animated elements.
 *
 * Keeps an element mounted during its CSS exit animation so it can fade/scale
 * out instead of vanishing instantly. Uses the `animationend` DOM event to know
 * when the exit animation finishes (with a safety timeout fallback).
 *
 * @param {boolean} isOpen - Whether the element should be visible
 * @returns {{ mounted: boolean, stage: 'entering'|'entered'|'exiting', ref: React.RefObject }}
 *   - mounted: render the element only when true
 *   - stage: use to pick enter vs exit animation class
 *   - ref: attach to the animated DOM element for animationend listening
 *
 * Usage:
 *   const { mounted, stage, ref } = useAnimatedPresence(isOpen);
 *   if (!mounted) return null;
 *   return (
 *     <div
 *       ref={ref}
 *       style={{
 *         animation: stage === 'exiting' ? EXIT_ANIM : ENTER_ANIM,
 *         pointerEvents: stage === 'exiting' ? 'none' : undefined,
 *       }}
 *     >
 *       ...
 *     </div>
 *   );
 */

function reducer(state, action) {
  switch (action.type) {
    case 'OPEN':
      return { mounted: true, stage: 'entering' };
    case 'ENTERED':
      return state.stage === 'entering' ? { ...state, stage: 'entered' } : state;
    case 'EXIT':
      return state.mounted ? { ...state, stage: 'exiting' } : state;
    case 'UNMOUNT':
      return { mounted: false, stage: 'exiting' };
    default:
      return state;
  }
}

export default function useAnimatedPresence(isOpen) {
  const [state, dispatch] = useReducer(reducer, {
    mounted: isOpen,
    stage: isOpen ? 'entering' : 'exiting',
  });
  const ref = useRef(null);
  const safetyTimerRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      if (safetyTimerRef.current !== null) {
        clearTimeout(safetyTimerRef.current);
        safetyTimerRef.current = null;
      }
      dispatch({ type: 'OPEN' });
    } else {
      dispatch({ type: 'EXIT' });
    }
  }, [isOpen]);

  // Listen for animationend to unmount after exit
  useEffect(() => {
    if (state.stage !== 'exiting') return;

    const el = ref.current;

    const handleAnimationEnd = (e) => {
      if (e.target === el) {
        if (safetyTimerRef.current !== null) {
          clearTimeout(safetyTimerRef.current);
          safetyTimerRef.current = null;
        }
        dispatch({ type: 'UNMOUNT' });
      }
    };

    if (el) {
      el.addEventListener('animationend', handleAnimationEnd);
    }

    safetyTimerRef.current = setTimeout(() => {
      safetyTimerRef.current = null;
      dispatch({ type: 'UNMOUNT' });
    }, 500);

    return () => {
      if (el) {
        el.removeEventListener('animationend', handleAnimationEnd);
      }
      if (safetyTimerRef.current !== null) {
        clearTimeout(safetyTimerRef.current);
        safetyTimerRef.current = null;
      }
    };
  }, [state.stage]);

  // Transition entering -> entered on the next frame
  useEffect(() => {
    if (state.stage === 'entering' && state.mounted) {
      const frame = requestAnimationFrame(() => {
        dispatch({ type: 'ENTERED' });
      });
      return () => cancelAnimationFrame(frame);
    }
  }, [state.stage, state.mounted]);

  return { mounted: state.mounted, stage: state.stage, ref };
}
